<template>
  <a
    v-if="link"
    class="pill-button"
    :href="link"
    :target="target"
    :rel="rel"
    :style="buttonStyle"
    @click="handleClick"
  >
    <span class="pill-button__shape" aria-hidden="true">
      <span class="pill-button__left"></span>
      <span class="pill-button__center"></span>
      <span class="pill-button__right">
        <svg class="pill-button__cap" viewBox="0 0 40 40">
          <path :d="shapePath" transform="translate(-80 0)" />
        </svg>
      </span>
    </span>
    <span class="pill-button__content">
      <slot>{{ content }}</slot>
    </span>
    <span class="pill-button__ball" aria-hidden="true"></span>
  </a>

  <button
    v-else
    class="pill-button"
    :type="type"
    :style="buttonStyle"
    @click="handleClick"
  >
    <span class="pill-button__shape" aria-hidden="true">
      <span class="pill-button__left"></span>
      <span class="pill-button__center"></span>
      <span class="pill-button__right">
        <svg class="pill-button__cap" viewBox="0 0 40 40">
          <path :d="shapePath" transform="translate(-80 0)" />
        </svg>
      </span>
    </span>
    <span class="pill-button__content">
      <slot>{{ content }}</slot>
    </span>
    <span class="pill-button__ball" aria-hidden="true"></span>
  </button>
</template>

<script setup lang="ts">
import { computed, type CSSProperties } from 'vue';

const shapePath = 'M0 20C0 8.95431 8.9543 0 20 0H93C96.3137 0 99 2.68629 99 6C99 14.2843 105.716 21 114 21H115.615C118.037 21 120 22.9631 120 25.3846C120 33.4565 113.456 40 105.385 40H20C8.95431 40 0 31.0457 0 20Z';

type CssLength = string | number;

const toCssLength = (value: CssLength | undefined, fallback: string) => (
  typeof value === 'number' ? String(value) + 'px' : value ?? fallback
);

const props = withDefaults(
  defineProps<{
    content?: string;
    link?: string;
    target?: '_self' | '_blank' | '_parent' | '_top';
    rel?: string;
    type?: 'button' | 'submit' | 'reset';
    buttonColor?: string;
    textColor?: string;
    paddingTop?: CssLength;
    paddingRight?: CssLength;
    paddingBottom?: CssLength;
    paddingLeft?: CssLength;
  }>(),
  {
    content: '',
    link: '',
    target: '_self',
    rel: undefined,
    type: 'button',
    buttonColor: '#222222',
    textColor: '#fff',
  },
);

const buttonStyle = computed<CSSProperties>(() => ({
  '--pill-button-color': props.buttonColor,
  '--pill-button-text-color': props.textColor,
  '--pill-button-padding-top': toCssLength(props.paddingTop, '.375em'),
  '--pill-button-padding-right': toCssLength(props.paddingRight, '24px'),
  '--pill-button-padding-bottom': toCssLength(props.paddingBottom, '.375em'),
  '--pill-button-padding-left': toCssLength(props.paddingLeft, '24px'),
}));

const emit = defineEmits<{
  click: [event: MouseEvent];
}>();

const handleClick = (event: MouseEvent) => {
  emit('click', event);
};
</script>

<style scoped lang="scss">
.pill-button {
  --pill-button-color: #222222;
  --pill-button-text-color: #fff;

  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  min-width: 130px;
  min-height: 24px;
  height: auto;
  padding:
    var(--pill-button-padding-top)
    var(--pill-button-padding-right)
    var(--pill-button-padding-bottom)
    var(--pill-button-padding-left);
  overflow: visible;
  color: var(--pill-button-text-color);
  font: inherit;
  font-weight: 700;
  line-height: 1;
  text-align: center;
  text-decoration: none;
  appearance: none;
  cursor: pointer;
  background: transparent;
  border: 0;

  &:focus-visible {
    outline: 2px solid var(--pill-button-color);
    outline-offset: 3px;
  }
}

.pill-button__shape {
  position: absolute;
  inset: 0;
  display: flex;
  pointer-events: none;
}

.pill-button__left,
.pill-button__right {
  flex: 0 0 auto;
  height: 100%;
  aspect-ratio: calc(7 / 10) / 1;
}

.pill-button__left {
  background: var(--pill-button-color);
  border-radius: 999px 0 0 999px;
}

.pill-button__center {
  flex: 1 1 auto;
  min-width: 0;
  background: var(--pill-button-color);
}

.pill-button__right {
  position: relative;
}

.pill-button__cap {
  position: absolute;
  top: 0;
  right: 0;
  display: block;
  width: auto;
  height: 100%;
  aspect-ratio: 1;
  shape-rendering: geometricPrecision;

  path {
    fill: var(--pill-button-color);
  }
}

.pill-button__content {
  position: relative;
  z-index: 1;
}

.pill-button__ball {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 2;
  width: auto;
  height: 50%;
  aspect-ratio: 1;
  pointer-events: none;
  background: var(--pill-button-color);
  border-radius: 50%;
  transform: translate(20%, -20%);
  transition: transform 300ms ease;
}

.pill-button:hover .pill-button__ball {
  transform: translate(20%, -40%);
}

@media (prefers-reduced-motion: reduce) {
  .pill-button__ball {
    transition: none;
  }
}

</style>
